import { useState, useEffect } from 'react';
import { listSourceGeneALL, listSourceVariantAll_VDAs } from '../../search/searchGrid/helpers/tableHelpers';
import { XMarkIcon } from '@heroicons/react/24/outline';
import { trackEnrichmentAction } from '../../../utils/analytics';

const EnrichmentModal = ({ isOpen, onClose, onSubmit, initialParams, searchType, notFoundItems, user }) => {
  const [params, setParams] = useState(initialParams);

  const isAcademic = user?.['cognito:groups'][0] === 'ROLE_ACADEMIC';

  useEffect(() => {
    setParams(initialParams);
  }, [initialParams]);

  // Filter sources based on user role
  const getFilteredSources = () => {
    const sourceList = searchType === 'GENES' ? listSourceGeneALL : listSourceVariantAll_VDAs;

    // If user is academic, only show CURATED
    if (isAcademic) {
      const filtered = {};
      Object.entries(sourceList).forEach(([group, options]) => {
        const curatedOptions = (options as string[]).filter(opt => opt === 'CURATED');
        if (curatedOptions.length > 0) {
          filtered[group] = curatedOptions;
        }
      });
      return filtered;
    }

    // For other users, show all sources
    return sourceList;
  };

  const filteredSources = getFilteredSources();

  const handleInputChange = (field, value) => {
    setParams(prev => ({ ...prev, [field]: value }));
  };

  const handleSubmit = e => {
    e.preventDefault();
    trackEnrichmentAction('run_enrichment_submit', 'enrichment_modal');
    onSubmit(params);
  };

  const handleReset = () => {
    setParams({
      commonVariants: 2,
      maxPvalue: 0.1,
      maxScore: 1,
      minScore: 0.1,
      pageNumber: 0,
      source: 'ALL',
    });
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg p-6 w-full max-w-md mx-4 max-h-[90vh] overflow-y-auto">
        <div className="flex justify-between items-center mb-6">
          <h2 className="text-xl font-bold text-gray-900">Enrichment Parameters</h2>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600 text-2xl leading-none">
            <XMarkIcon className="h-5 w-5 text-black" />
          </button>
        </div>

        {notFoundItems.length > 0 && (
          <span className="warning-message p-2 text-center block w-full">
            These IDs are not found: <strong>{notFoundItems.join(', ')}</strong>
          </span>
        )}

        <form onSubmit={handleSubmit} className="space-y-4 mt-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              {searchType === 'GENES' ? 'Common Genes' : 'Common Variants'}
            </label>
            <input
              type="number"
              value={params.commonVariants}
              onChange={e => handleInputChange('commonVariants', parseInt(e.target.value, 10) || 0)}
              className="w-full p-2 border border-gray-300 rounded-md"
              min="0"
            />
            <p className="text-xs text-gray-500 mt-1">
              Minimum number of common {searchType === 'GENES' ? 'genes' : 'variants'} required
            </p>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Max P-value</label>
            <input
              type="number"
              step="0.01"
              value={params.maxPvalue}
              onChange={e => handleInputChange('maxPvalue', parseFloat(e.target.value) || 0)}
              className="w-full p-2 border border-gray-300 rounded-md"
              min="0"
              max="1"
            />
            <p className="text-xs text-gray-500 mt-1">Maximum p-value threshold (0-1)</p>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Max Score</label>
            <input
              type="number"
              step="0.1"
              value={params.maxScore}
              onChange={e => handleInputChange('maxScore', parseFloat(e.target.value) || 0)}
              className="w-full p-2 border border-gray-300 rounded-md"
              min="0"
            />
            <p className="text-xs text-gray-500 mt-1">Maximum score threshold</p>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Min Score</label>
            <input
              type="number"
              step="0.1"
              value={params.minScore}
              onChange={e => handleInputChange('minScore', parseFloat(e.target.value) || 0)}
              className="w-full p-2 border border-gray-300 rounded-md"
              min="0"
            />
            <p className="text-xs text-gray-500 mt-1">Minimum score threshold</p>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Source</label>

            <select
              value={params.source}
              onChange={e => handleInputChange('source', e.target.value)}
              className="w-full p-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            >
              {Object.entries(filteredSources).map(([group, options]) => (
                <optgroup key={group} label={group}>
                  {(options as string[]).map(opt => (
                    <option key={opt} value={opt}>
                      {opt}
                    </option>
                  ))}
                </optgroup>
              ))}
            </select>

            <p className="text-xs text-gray-500 mt-1">Data source for enrichment analysis</p>
          </div>

          <div className="flex pt-4">
            <button type="submit" className="filter-upload">
              Run Enrichment
            </button>
            <button type="button" onClick={handleReset} className="filter-upload">
              Reset
            </button>
            <button type="button" onClick={onClose} className="filter-upload">
              Cancel
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default EnrichmentModal;
